/**
 * Renders a trusted HTML body (a `body_html` field) with the site's styling.
 * Used by Promotions, Resources, Recognition, and What's new.
 *
 * The server sanitizes `body_html` on write with the shared allowlist in
 * `@api/richtext` (api#546), which drops scripts, event handlers,
 * `javascript:` URLs, and any image src that is not our own image route. That
 * is what makes dangerouslySetInnerHTML safe here. Do not point this at HTML
 * from any other source.
 *
 * IMAGES. The api stores an editor image as a RELATIVE src
 * (`/v1/core/images/{id}`) so a body carries no stage or hostname and stays
 * portable between environments. The api is its own origin, though, so a
 * relative src here would resolve against The Sweet Spot and return the app
 * shell instead of an image. `withApiImageSrc` prefixes the API base at render
 * time. Cockpit does the same thing in `ui/src/lib/richTextHtml.ts`.
 *
 * The class list below covers everything the shared editor can emit (headings,
 * lists, checklists, quote, code, rules, tables, images), and mirrors Cockpit's
 * `amp-rich-text` rules so the same entry reads the same way in both apps.
 */

import { API_BASE } from '@/lib/apiBase';

const RELATIVE_IMAGE_SRC = /(<img\b[^>]*?\bsrc=")(\/v1\/core\/images\/\d+)(")/gi;

function withApiImageSrc(html: string): string {
  return html.replace(
    RELATIVE_IMAGE_SRC,
    (_m, pre: string, path: string, post: string) => `${pre}${API_BASE}${path}${post}`,
  );
}

const BODY_CLASSES = [
  'text-sm text-candy-ink dark:text-candy-dark-ink',
  // Links
  '[&_a]:text-candy-teal [&_a]:underline-offset-2 [&_a]:hover:underline dark:[&_a]:text-candy-teal-dark',
  // Headings
  '[&_h1]:mb-1 [&_h1]:text-[17px] [&_h1]:font-bold',
  '[&_h2]:mb-1 [&_h2]:text-[15px] [&_h2]:font-semibold',
  '[&_h3]:mb-1 [&_h3]:text-[14px] [&_h3]:font-semibold',
  // Text and lists
  '[&_p]:my-1 [&_li]:ml-4 [&_ol]:my-1 [&_ol]:list-decimal [&_ul]:my-1 [&_ul]:list-disc',
  // Checklists carry their own checkbox, so they take no marker.
  "[&_ul[data-type='taskList']]:list-none [&_ul[data-type='taskList']]:ml-0",
  "[&_ul[data-type='taskList']_li]:flex [&_ul[data-type='taskList']_li]:gap-2 [&_ul[data-type='taskList']_li]:ml-0",
  // Quote
  '[&_blockquote]:border-l-[3px] [&_blockquote]:border-candy-line [&_blockquote]:pl-3 [&_blockquote]:italic',
  'dark:[&_blockquote]:border-candy-dark-line',
  // Code
  '[&_code]:rounded [&_code]:bg-candy-cream-warm [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.9em]',
  'dark:[&_code]:bg-candy-dark-surface-warm',
  '[&_pre]:my-1 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-candy-cream-warm [&_pre]:p-2',
  'dark:[&_pre]:bg-candy-dark-surface-warm [&_pre_code]:bg-transparent [&_pre_code]:p-0',
  // Rules
  '[&_hr]:my-3 [&_hr]:border-t [&_hr]:border-candy-line dark:[&_hr]:border-candy-dark-line',
  // Images never overflow the card on a phone.
  '[&_img]:my-2 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-md',
  // Tables scroll rather than stretch the card.
  '[&_table]:my-2 [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_table]:border-collapse',
  '[&_td]:border [&_td]:border-candy-line [&_td]:px-2 [&_td]:py-1 [&_td]:align-top',
  '[&_th]:border [&_th]:border-candy-line [&_th]:bg-candy-cream-warm [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold',
  'dark:[&_td]:border-candy-dark-line dark:[&_th]:border-candy-dark-line dark:[&_th]:bg-candy-dark-surface-warm',
].join(' ');

/**
 * Wraps a body that was saved before its box became a rich-text one.
 *
 * Those rows hold plain text, and some of it has real line breaks in it that
 * the reader typed and expects to see. Dropped into HTML as-is, every one of
 * those breaks would collapse into a single space. A blank line becomes a new
 * paragraph and a single line break becomes a break, which is what the writer
 * meant. Ampersands are escaped so a literal "&amp;" in old text is not read
 * back as an entity; angle brackets cannot appear here, because their presence
 * is what marks a body as HTML in the first place.
 */
function plainTextToHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .split(/\n{2,}/)
    .map((para) => `<p>${para.replace(/\n/g, '<br />')}</p>`)
    .join('');
}

export function HtmlBody({ src }: { src: string }) {
  // No tags at all means this was written before the editor existed.
  const html = src.includes('<') ? src : plainTextToHtml(src);
  return <div className={BODY_CLASSES} dangerouslySetInnerHTML={{ __html: withApiImageSrc(html) }} />;
}
